import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Text, YStack, XStack } from "tamagui";
import { GradientButton, OutlinedButton } from "tamagui-config";

import ConfigureIntegrationDialog from "./ConfigureIntegrationDialog";
import {
  serviceTitanLogo,
  googleLogo,
  epaLogo,
  airNowLogo,
  simProLogo,
  fieldPulseLogo,
  buildEdgeLogo,
  zillowLogo,
  redfinLogo,
} from "../../assets/images";
import { StatusPill } from "../../components/StatusPill";

type IntegrationDialogInfo = {
  id: string;
  title: string;
  description: string;
};

const Integrations = () => {
  const { t } = useTranslation();
  const [isConfigureDialogOpen, setConfigureDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationDialogInfo | null>(null);

  const [connections, setConnections] = useState<{ [key: string]: boolean }>(
    {},
  );

  // Reusable handler functions to reduce code duplication
  const handleConnect = (id: string, title: string, description: string) => {
    setSelectedIntegration({ id, title, description });
    setConfigureDialogOpen(true);
  };

  const handleManage = (id: string, title: string, description: string) => {
    setSelectedIntegration({ id, title, description });
    setConfigureDialogOpen(true);
  };
  const integrationsConfig = [
    {
      id: "serviceTitan",
      logo: serviceTitanLogo,
      title: "Service Titan",
      description: t("integrations.descriptions.serviceTitan"),
      isConnected: connections.serviceTitan || false,
      onConnect: () =>
        handleConnect(
          "serviceTitan",
          "Configure ServiceTitan Sync",
          "The following items will be sent to ServiceTitan.",
        ),
      onManage: () =>
        handleManage(
          "serviceTitan",
          "Configure ServiceTitan Sync",
          "The following items will be sent to ServiceTitan.",
        ),
    },
    {
      id: "google",
      logo: googleLogo,
      title: "Google",
      description: t("integrations.descriptions.googleSSO"),
      isConnected: connections.google || false,
      onConnect: () =>
        handleConnect(
          "google",
          "Configure Google Sync",
          "The following items will be sent to Google.",
        ),
      onManage: () =>
        handleManage(
          "google",
          "Configure Google Sync",
          "The following items will be sent to Google.",
        ),
    },
    {
      id: "epa",
      logo: epaLogo,
      title: "EPA.gov",
      description: "AQI (Air Quality Index) Integration",
      isConnected: connections.epa || false,
      onConnect: () =>
        handleConnect(
          "epa",
          "Configure EPA.gov Sync",
          "The following items will be sent to EPA.gov.",
        ),
      onManage: () =>
        handleManage(
          "epa",
          "Configure EPA.gov Sync",
          "The following items will be sent to EPA.gov.",
        ),
    },
    {
      id: "airNow",
      logo: airNowLogo,
      title: "AirNow",
      description: "Air Quality data, fire and smoke / map integrations",
      isConnected: connections.airNow || false,
      onConnect: () =>
        handleConnect(
          "airNow",
          "Configure AirNow Sync",
          "The following items will be sent to AirNow.",
        ),
      onManage: () =>
        handleManage(
          "airNow",
          "Configure AirNow Sync",
          "The following items will be sent to AirNow.",
        ),
    },
    {
      id: "simPro",
      logo: simProLogo,
      title: "SimPro",
      description: "Field management software integration",
      isConnected: connections.simPro || false,
      onConnect: () =>
        handleConnect(
          "simPro",
          "Configure SimPro Sync",
          "The following items will be sent to SimPro.",
        ),
      onManage: () =>
        handleManage(
          "simPro",
          "Configure SimPro Sync",
          "The following items will be sent to SimPro.",
        ),
    },
    {
      id: "fieldPulse",
      logo: fieldPulseLogo,
      title: "FieldPulse",
      description: "Field management software integration",
      isConnected: connections.fieldPulse || false,
      onConnect: () =>
        handleConnect(
          "fieldPulse",
          "Configure FieldPulse Sync",
          "The following items will be sent to FieldPulse.",
        ),
      onManage: () =>
        handleManage(
          "fieldPulse",
          "Configure FieldPulse Sync",
          "The following items will be sent to FieldPulse.",
        ),
    },
    {
      id: "buildEdge",
      logo: buildEdgeLogo,
      title: "BuildEdge",
      description: "Cost estimation integration and land surveys",
      isConnected: connections.buildEdge || false,
      onConnect: () =>
        handleConnect(
          "buildEdge",
          "Configure BuildEdge Sync",
          "The following items will be sent to BuildEdge.",
        ),
      onManage: () =>
        handleManage(
          "buildEdge",
          "Configure BuildEdge Sync",
          "The following items will be sent to BuildEdge.",
        ),
    },
    {
      id: "zillow",
      logo: zillowLogo,
      title: "Zillow",
      description:
        "Real estate data integration. Bridge integration for public home information and Zestimates",
      isConnected: connections.zillow || false,
      onConnect: () =>
        handleConnect(
          "zillow",
          "Configure Zillow Sync",
          "The following items will be sent to Zillow.",
        ),
      onManage: () =>
        handleManage(
          "zillow",
          "Configure Zillow Sync",
          "The following items will be sent to Zillow.",
        ),
    },
    {
      id: "redFin",
      logo: redfinLogo,
      title: "RedFin",
      description: "Housing Market Data Integration",
      isConnected: connections.redFin || false,
      onConnect: () =>
        handleConnect(
          "redFin",
          "Configure RedFin Sync",
          "The following items will be sent to RedFin.",
        ),
      onManage: () =>
        handleManage(
          "redFin",
          "Configure RedFin Sync",
          "The following items will be sent to RedFin.",
        ),
    },
  ];

  return (
    <YStack gap="$4">
      <YStack gap="$2">
        <Text fontSize="$5.5">{t("sidebar.settings.settings")}</Text>
        <Heading fontSize="$8">{t("sidebar.settings.integrations")}</Heading>
      </YStack>
      <YStack gap="$3.5">
        {integrationsConfig.map((integration) => (
          <YStack
            key={integration.id}
            backgroundColor="$gray4"
            borderRadius="$6"
            padding="$3.5"
            gap="$5"
            justifyContent="flex-start"
          >
            <XStack gap="$2" justifyContent="space-between" alignItems="center">
              <YStack
                style={{
                  height: 44,
                  width: "70%",
                  backgroundImage: `url(${integration.logo})`,
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "left",
                  display: "flex",
                }}
                aria-label={t("sidebar.logoAlt")}
              />
              {integration.isConnected && (
                <StatusPill success hideDot height="auto">
                  {t("integrations.connected")}
                </StatusPill>
              )}
            </XStack>
            <XStack justifyContent="space-between" alignItems="center">
              <YStack gap="$2">
                <Text fontSize="$5.5" fontWeight="$5">
                  {integration.title}
                </Text>
                <Text>{integration.description}</Text>
              </YStack>
              {integration.isConnected ? (
                <OutlinedButton
                  elevate
                  rounded
                  fontSize="$3"
                  onPress={integration.onManage}
                >
                  {t("integrations.manage")}
                </OutlinedButton>
              ) : (
                <GradientButton
                  elevated
                  rounded
                  fontSize="$3"
                  onPress={integration.onConnect}
                >
                  {t("integrations.connect")}
                </GradientButton>
              )}
            </XStack>
          </YStack>
        ))}
      </YStack>
      <ConfigureIntegrationDialog
        open={isConfigureDialogOpen}
        onOpenChange={async (isOpen) => {
          if (!isOpen) {
            setConfigureDialogOpen(false);
            setSelectedIntegration(null);
          }
        }}
        confirmButtonText={t("integrations.authorize")}
        onConfirm={async () => {
          try {
            // TODO: Replace with actual API call
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return true;
          } catch (error) {
            console.error("Authorization failed:", error);
            return false;
          }
        }}
        title={
          selectedIntegration
            ? selectedIntegration.title
            : "Configure Integration"
        }
        description={selectedIntegration ? selectedIntegration.description : ""}
        integrationTitle={
          selectedIntegration
            ? integrationsConfig.find(
                (config) => config.id === selectedIntegration.id,
              )?.title
            : undefined
        }
        onSuccess={() => {
          if (selectedIntegration) {
            setConnections((prev) => ({
              ...prev,
              [selectedIntegration.id]: true,
            }));
          }
        }}
      />
    </YStack>
  );
};
export default Integrations;
